import type { Background } from '@character-creator/types';

/**
 * Ignus Background Data
 *
 * Contains all backgrounds specific to the Ignus ruleset.
 * These are customized backgrounds from "The Backgrounds of Ignus" source document.
 */
export const ignusBackgrounds: Background[] = [
  {
    id: 'child-of-privilege',
    name: 'Child of Privilege',
    description:
      'You grew up in high society. Maybe you were the child of an important political figure or the scion of a venerated noble house. However you got there, your position of privilege has given you access to skills, knowledge, and education that many other adventurers don\'t have.',
    skillProficiencies: [],
    toolProficiencies: ["calligrapher's supplies", "cartographer's tools"],
    languages: 2,
    equipment: [
      "Calligrapher's supplies",
      "Cartographer's tools",
      'A small hand mirror',
      'Letter sealing wax',
      'A sealing stamp with your family\'s crest',
    ],
    feature: {
      name: 'Socially Adept',
      description:
        'Because of your upbringing, you are at home in high society. You are comfortable and at-ease in formal situations, understand the rules and manners expected of the well-off, and are able to navigate the complex social power dynamics that often accompany the nobility. You have advantage on social skill checks in formal settings.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I carry myself with an air of natural authority.',
        'I genuinely care about the well-being of those less fortunate.',
        'I expect the best of everything and am uncomfortable with less.',
        'I use my influence to protect those who cannot protect themselves.',
      ],
      ideals: [
        'Noblesse Oblige. Privilege comes with responsibility to others. (Good)',
        'Tradition. The old ways have value and should be preserved. (Lawful)',
        'Ambition. I will rise to even greater heights of power. (Neutral)',
        'Reform. Society must change, and I have the influence to help. (Chaotic)',
      ],
      bonds: [
        'My family\'s honor is everything to me.',
        'I owe a debt to those who helped me when I was young.',
        'I will prove myself worthy of my family name.',
        'I seek to restore my family\'s lost prestige.',
      ],
      flaws: [
        'I look down on those of lesser station.',
        'I am unused to hardship and struggle with discomfort.',
        'I expect others to cater to my needs.',
        'I am blind to my own privilege.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'master-merchant',
    name: 'Master Merchant',
    description:
      'You have always had a gift for working a deal and you\'ve used that gift to carve out a small commercial empire for yourself. You\'ve become skilled at opening new lines of trade and discovering goods and services that people didn\'t know they needed.',
    skillProficiencies: ['insight'],
    toolProficiencies: ["jeweler's tools"],
    languages: 2,
    equipment: [
      "Jeweler's tools",
      'Pen and ink',
      'A large leather-bound book of contacts and transactions',
    ],
    feature: {
      name: 'Experienced Negotiator',
      description:
        'As a master of commerce yourself, you understand the mind of a merchant. As such, you\'re generally able to negotiate lower prices for goods and services and more favorable terms when working out new trade deals. You have advantage on Persuasion checks to purchase goods or during any sort of negotiation.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I see opportunity in every situation.',
        'I keep careful track of debts owed to me.',
        'A handshake is as good as a contract to me.',
        'I treat everyone as a potential customer.',
      ],
      ideals: [
        'Fair Trade. An honest deal benefits everyone. (Good)',
        'Profit. The bottom line is what matters. (Neutral)',
        'Reputation. My word is my bond. (Lawful)',
        'Opportunity. Rules are meant to be bent. (Chaotic)',
      ],
      bonds: [
        'My business is my legacy.',
        'I owe everything to my first mentor.',
        'My trade network is my family.',
        'I seek the one deal that will secure my future.',
      ],
      flaws: [
        'I can\'t resist a good deal, even when I shouldn\'t.',
        'I measure people by their wealth.',
        'I hold grudges against those who\'ve cheated me.',
        'I take unnecessary risks for potential profit.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'miner',
    name: 'Miner',
    description:
      'You grew up in a rough-and-tumble mine town and your family worked the mines, breaking stone and revealing its treasures. The caverns, tunnels and shafts of the mine were your playground when you were young, and, as you got older, you learned how to handle the tools of the trade.',
    skillProficiencies: [],
    toolProficiencies: ["mason's tools"],
    languages: 1,
    equipment: ["Mason's tools", 'A pick', 'A shovel'],
    feature: {
      name: 'Child of Stone',
      description:
        'As someone who spent a large portion of your life underground, you have an instinctual understanding of stone, stonework, and structural engineering. The kids that didn\'t often didn\'t survive the mines, after all. You have advantage on Perception and Investigation checks related to structural integrity, stonework, or geology.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I\'m most comfortable underground.',
        'I can read the language of stone.',
        'Hard work is its own reward.',
        'I distrust those who\'ve never gotten their hands dirty.',
      ],
      ideals: [
        'Community. We look out for each other down in the dark. (Good)',
        'Honest Labor. There\'s dignity in hard work. (Lawful)',
        'Discovery. Every day might reveal something new. (Chaotic)',
        'Wealth. The earth holds riches for those who dig deep enough. (Neutral)',
      ],
      bonds: [
        'My mining community is my family.',
        'I seek to find what my ancestors discovered.',
        'I carry a piece of ore from my first dig.',
        'I must support those I left behind.',
      ],
      flaws: [
        'I\'m claustrophobic in open spaces.',
        'I hoard precious stones compulsively.',
        'I\'m suspicious of surface dwellers.',
        'I take foolish risks underground.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'well-connected',
    name: 'Well-Connected',
    description:
      'Your family is well-known and well-connected even if they\'re not exactly upper-crust material. Some people might respect them, some might fear them, some might owe them money or a favor. Whatever the reason, you have pull with certain people. Not that you rely on your family\'s influence. You grew up steeped in the world of influence-trading and have your own, extensive network of friends, contacts, business associates, and people who owe you favors.',
    skillProficiencies: ['insight'],
    toolProficiencies: [],
    languages: 0,
    equipment: [
      'A small leather-bound notebook written in your own personal cypher where you record observations, track favors, and make note of interesting bits of information',
    ],
    feature: {
      name: 'I Know a Guy',
      description:
        'Between your family and your own work, you have such an extensive network of influence that, whatever you need or wherever you are, it\'s likely that you know someone who can help you out.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I remember every favor owed to me.',
        'I make friends wherever I go.',
        'Information is my currency.',
        'I never reveal all my cards.',
      ],
      ideals: [
        'Loyalty. Those in my network can count on me. (Good)',
        'Reciprocity. Every favor given expects one in return. (Lawful)',
        'Flexibility. Rules are suggestions to be worked around. (Chaotic)',
        'Self-Interest. My network serves my purposes. (Neutral)',
      ],
      bonds: [
        'My contact network is my life\'s work.',
        'I owe my family for my connections.',
        'Someone in my network holds a secret I need.',
        'I must protect my most valuable contacts.',
      ],
      flaws: [
        'I can\'t resist gathering information.',
        'I trust my network too much.',
        'I have enemies among my former contacts.',
        'I use people without considering their feelings.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'woodwise',
    name: 'Woodwise',
    description:
      'You grew up in an isolated community in one of the wild areas of Ignus and your family relied on hunting and trapping to survive outside of the bounds of normal society. You\'re not just at home in the wilderness, you thrive in it.',
    skillProficiencies: ['nature', 'survival'],
    toolProficiencies: [],
    languages: 1,
    equipment: ["Trapper's tools"],
    feature: {
      name: 'Student of the Wild',
      description:
        'Surviving and thriving in the wilderness means understanding the environment and the creatures that live in it. You have advantage on Investigation checks to investigate natural objects as well as things made with natural, organic materials. You also have advantage on checks to identify birds and other animals as long as you can see or hear them.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I\'m more comfortable with animals than people.',
        'I can predict the weather by reading nature.',
        'I respect the balance of the natural world.',
        'Cities make me nervous and uncomfortable.',
      ],
      ideals: [
        'Nature. The wild world must be protected. (Good)',
        'Balance. Take only what you need. (Lawful)',
        'Freedom. The wilderness knows no rules. (Chaotic)',
        'Survival. Only the strong survive. (Neutral)',
      ],
      bonds: [
        'My wilderness home is sacred to me.',
        'I learned everything from an elder in my community.',
        'I must protect the wild places from civilization.',
        'A particular animal is my spiritual guide.',
      ],
      flaws: [
        'I distrust civilization and its trappings.',
        'I\'m uncomfortable in social situations.',
        'I judge city folk harshly.',
        'I\'m easily lost in urban environments.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'domestic-manager',
    name: 'Domestic Manager',
    description:
      'Growing up, you spent a lot of time at home, helping with domestic responsibilities. Whether that\'s because of illness, disability, family or cultural dynamics, or personal preference, you ended up with a host of interesting and exceptionally useful skills for navigating everyday life.',
    skillProficiencies: [],
    toolProficiencies: [
      "cook's utensils",
      "leatherworker's tools",
      "tinker's tools",
      "weaver's tools",
    ],
    languages: 0,
    equipment: ["Cook's utensils", "Leatherworker's tools", "Tinker's tools"],
    feature: {
      name: 'Good with Kids',
      description:
        'You spent a lot of time helping care for younger members of your family and community growing up. As such, you understand how to interact with kids of all ages and species. You\'re also one of their greatest defenders, standing up for them when few others will. You have advantage on Persuasion and Insight checks involving children as well as advantage on Persuasion and Intimidation checks to protect children.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I can make a home anywhere.',
        'I notice when others need care.',
        'I find satisfaction in everyday tasks.',
        'I\'m patient and methodical.',
      ],
      ideals: [
        'Nurturing. Everyone deserves care and comfort. (Good)',
        'Order. A well-run home is a happy home. (Lawful)',
        'Creativity. There\'s always a new way to solve a problem. (Chaotic)',
        'Practicality. Function over form. (Neutral)',
      ],
      bonds: [
        'My family depends on me.',
        'I learned my skills from someone I deeply respect.',
        'I care for those who cannot care for themselves.',
        'My home is my sanctuary.',
      ],
      flaws: [
        'I can\'t leave a mess uncleaned.',
        'I mother everyone around me.',
        'I undervalue my own contributions.',
        'I worry excessively about others.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'life-long-learner',
    name: 'Life Long Learner',
    description:
      'You have spent your life with a voracious appetite for knowledge. As a child you apprenticed with every professional in your village learning the ins and outs of dozens of artisan, business, and leadership trades. Your continual dedication to learning and gathering knowledge has given you a working knowledge across a vast array of topics.',
    skillProficiencies: ['history', 'investigation'],
    toolProficiencies: [],
    languages: 3,
    equipment: [
      "Scholar's pack",
      'A library card',
      "Calligrapher's or painter's supplies",
    ],
    feature: {
      name: 'Never Stop Learning',
      description:
        'You are dedicated to continuous learning, and when you find topics about which you don\'t already have a working knowledge, you have the scholarly status to gain access to institutions of knowledge and lore such as libraries, archives, and laboratories for the purposes of research within the confines of the institution\'s rules and regulations. Additionally, you have advantage on all History or Investigation checks related to academic research.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I\'m always reading something.',
        'I ask questions constantly.',
        'I get excited about obscure topics.',
        'I can\'t resist correcting misinformation.',
      ],
      ideals: [
        'Knowledge. Learning is its own reward. (Good)',
        'Preservation. Knowledge must be protected. (Lawful)',
        'Discovery. There\'s always something new to learn. (Chaotic)',
        'Understanding. Knowledge is power. (Neutral)',
      ],
      bonds: [
        'My collection of notes is my greatest treasure.',
        'I owe my curiosity to a great teacher.',
        'I seek a specific piece of lost knowledge.',
        'I must share what I learn with others.',
      ],
      flaws: [
        'I get lost in research and forget the real world.',
        'I\'m condescending to the less educated.',
        'I can\'t resist acquiring rare books or scrolls.',
        'I trust written sources too readily.',
      ],
    },
    source: 'ignus',
  },
  {
    id: 'bartom-keeper',
    name: 'Bartom Keeper',
    description:
      'You grew up surrounded by the natural world, deeply connected to nature in the way that only a child of the Circles can understand. But even they couldn\'t understand how connected you were to nature. At a young age, you would often find yourself singing along with a song that nobody else heard or following along with a conversation that nobody else seemed to know was happening around them. It wasn\'t until your teenage years that you realized that you were hearing the thoughts, songs, and words of the giant, magical Bartom trees of Ignus. Once the druids realized your innate connection to the Bartom trees, they named you Keeper, an honor usually reserved for much older and wiser druids and taught you the secret ways to care for these sacred giants and even make new ones.',
    skillProficiencies: ['nature', 'survival'],
    toolProficiencies: [],
    languages: 1,
    equipment: [
      'A small magical box made of Bartom wood containing a dozen Bartom seeds',
      'A small kit of specialized tools for the care of trees',
    ],
    feature: {
      name: 'Bartom Affinity',
      description:
        'The root system of a Bartom tree stretches many, many miles and their influence on the natural world around them stretches even further. You can sense the presence of any Bartom tree within a 50-mile radius of your location. When you do so, you know how far away it is, what direction, and gain clues to its mental and emotional state. Once you\'re within a Bartom\'s root radius, you have the ability to communicate directly with it.',
    },
    suggestedCharacteristics: {
      personalityTraits: [
        'I often seem distracted, listening to voices others can\'t hear.',
        'I speak to plants as if they were old friends.',
        'I feel the pain of dying trees.',
        'I move with the patience of deep roots.',
      ],
      ideals: [
        'Stewardship. The Bartom trees are my sacred charge. (Good)',
        'Harmony. All life is connected through nature. (Neutral)',
        'Preservation. The old trees hold ancient wisdom. (Lawful)',
        'Growth. New Bartom trees mean new hope for the world. (Chaotic)',
      ],
      bonds: [
        'I have a special bond with one particular Bartom tree.',
        'The druids who trained me are my true family.',
        'I must plant new Bartom trees wherever I go.',
        'I carry the songs of the Bartom in my heart.',
      ],
      flaws: [
        'I prioritize trees over people.',
        'I struggle to connect with non-natural beings.',
        'I become devastated when trees are harmed.',
        'I can be manipulated through threats to the Bartom.',
      ],
    },
    source: 'ignus',
  },
];
